import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

interface ToggleWithModalProps<T> {
  name: string;
  value: T;
  onValue: T;
  offValue: T;
  onChange: (value: T) => void;
  label: string;
  showModal?: boolean;
  modalTitle?: string;
  modalDescription?: string;
  modalConfirmText?: string;
  modalCancelText?: string;
  className?: string;
}

export const ToggleWithModal = <T extends string | number | boolean>({
  name,
  value,
  onValue,
  offValue,
  onChange,
  label,
  showModal = true,
  modalTitle,
  modalDescription,
  modalConfirmText,
  modalCancelText,
  className
}: ToggleWithModalProps<T>) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { t } = useTranslation();

  const handleToggle = () => {
    if (showModal) {
      setShowConfirmationModal(true);
    } else {
      onChange(value === onValue ? offValue : onValue);
    }
  };

  const handleConfirm = () => {
    onChange(value === onValue ? offValue : onValue);
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
  };

  return (
    <>
      <Form.Group controlId={name} className={className}>
        <Form.Check
          type="switch"
          id={name}
          label={label}
          checked={value === onValue}
          onChange={handleToggle}
        />
      </Form.Group>

      {showModal && (
        <Modal 
          show={showConfirmationModal} 
          centered 
          onHide={handleCancel}
          aria-modal="true"
          aria-labelledby={`${name}-modal-title`}
          aria-describedby={`${name}-modal-description`}
        >
          <Modal.Header closeButton>
            <Modal.Title id={`${name}-modal-title`}>
              {modalTitle || t('toggleWithModal.confirmTitle')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body id={`${name}-modal-description`}>
            {modalDescription || t('toggleWithModal.confirmDescription')}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-primary" onClick={handleCancel}>
              {modalCancelText || t('toggleWithModal.cancel')}
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              {modalConfirmText || t('toggleWithModal.confirm')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default ToggleWithModal; 